import * as React from "react";
import { Link } from "react-router-dom";
import { usePostFlowContext } from "@/components/PostDialogs/SharedPostFlowContext";
import { Button, Popover } from "@/components/ui";
import {
  DEFAULT_REVIEW_URL_STATE,
  serializeCoachReviewSearch,
} from "@/containers/Coach/Reputation/Reviews/components/reviewFiltersSearchParams";
import { URLS } from "@/utils/routes";
import { Review } from "@/services/bedrock/dtos/Review";
import { isLibraryEntry, useShareFlow } from "./ShareFlowProvider";
import { ReviewHeaderLine, ReviewRowSkeleton, ReviewSummaryLine } from "./ReviewPickerRow";
import { EditQuoteTile, isQuoteEditable, ReviewQuoteLine } from "./quote/HighlightedQuote";

/**
 * The review a single-review post is about, with the quote the image shows and the way to edit it.
 * From the library it starts on the player's best review and can be changed.
 */
export const ReviewCard: React.FC<{ onEditQuote: (review: Review) => void }> = ({ onEditQuote }) => {
  const flow = useShareFlow();
  const { onBehalfOf, effectivePlayer } = usePostFlowContext();
  const [open, setOpen] = React.useState(false);
  const libraryEntry = isLibraryEntry(flow.entry);
  const leadId = flow.reviewIds[0];
  const lead = leadId != null ? flow.reviewById(leadId) : undefined;

  // In a carousel the rows carry the reviews; the first one is this review.
  if (flow.mode === "carousel") return null;

  if (!lead && (flow.postableLoading || (flow.pinnedId != null && !flow.pinnedReview && !flow.pinnedReviewFailed))) {
    return (
      <div className="flex flex-col gap-2" data-testid="lead-review-loading">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Review</span>
        <ReviewRowSkeleton numbered={false} />
      </div>
    );
  }

  if (libraryEntry && flow.postableReviews.length === 0) {
    if (onBehalfOf) {
      const name = effectivePlayer?.fullName || "this player";
      const reviewsUrl = `${URLS.coach.reputation.reviews}?${serializeCoachReviewSearch({
        ...DEFAULT_REVIEW_URL_STATE,
        playerId: flow.playerId,
      })}`;
      return (
        <p className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-500">
          No reviews are ready to share for {name} yet.{" "}
          <Link to={reviewsUrl} className="font-semibold text-primary hover:underline" onClick={flow.close}>
            See their reviews
          </Link>
        </p>
      );
    }
    return (
      <p className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-500">
        You don't have reviews ready to share yet.
      </p>
    );
  }

  if (!lead) return null;

  return (
    <div className="flex flex-col gap-2" data-testid="review-card">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Review</span>
        {libraryEntry && flow.postableReviews.length > 1 && (
          <Popover open={open} onOpenChange={setOpen}>
            <Popover.Trigger
              render={
                <Button variant="link" size="sm" className="px-0">
                  Change review
                </Button>
              }
            />
            <Popover.Content className="w-[min(90vw,420px)] p-2">
              <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
                {flow.postableReviews.map(review => (
                  <li key={review.id}>
                    <button
                      type="button"
                      aria-pressed={Number(review.id) === leadId}
                      className="w-full rounded-lg p-2 text-left hover:bg-slate-50 cursor-pointer"
                      onClick={() => {
                        flow.selectLeadReview(Number(review.id));
                        setOpen(false);
                      }}
                    >
                      <ReviewSummaryLine review={review} />
                    </button>
                  </li>
                ))}
              </ul>
            </Popover.Content>
          </Popover>
        )}
      </div>
      <div className="flex items-stretch gap-3 rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <ReviewHeaderLine review={lead} />
          <ReviewQuoteLine
            review={lead}
            edit={flow.quoteEdits[Number(lead.id)]}
            onEdit={isQuoteEditable(lead) ? () => onEditQuote(lead) : undefined}
          />
        </div>
        {isQuoteEditable(lead) && <EditQuoteTile onClick={() => onEditQuote(lead)} />}
      </div>
    </div>
  );
};
